/**
 * Highlight.io 유틸리티 함수들
 * 버그 트래킹 및 사용자 행동 분석을 위한 헬퍼 함수들
 */

import { H } from 'highlight.run';

/**
 * 사용자 세션에 메타데이터 추가
 * @param {Object} metadata - 추가할 메타데이터
 */
export const setUserMetadata = (metadata) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.identify(metadata.userId, metadata);
  }
};

/**
 * 커스텀 이벤트 추적
 * @param {string} eventName - 이벤트 이름
 * @param {Object} properties - 이벤트 속성
 */
export const trackEvent = (eventName, properties = {}) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.track(eventName, properties);
  }
};

/**
 * 에러 추적
 * @param {Error} error - 에러 객체
 * @param {string} message - 에러 메시지
 * @param {Object} context - 에러 컨텍스트
 */
export const trackError = (error, message = 'Custom Error', context = {}) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.consumeError(error, message, {
      ...context
    });
  }
};

/**
 * 페이지 뷰 추적
 * @param {string} pageName - 페이지 이름
 * @param {Object} properties - 페이지 속성
 */
export const trackPageView = (pageName, properties = {}) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.track('page_view', {
      page: pageName,
      ...properties
    });
  }
};

/**
 * API 호출 추적
 * @param {string} endpoint - API 엔드포인트
 * @param {string} method - HTTP 메서드
 * @param {number} statusCode - 응답 상태 코드
 * @param {number} duration - 응답 시간 (ms)
 */
export const trackApiCall = (endpoint, method, statusCode, duration) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.track('api_call', {
      endpoint,
      method,
      statusCode,
      duration,
      success: statusCode >= 200 && statusCode < 400
    });
  }
};

/**
 * 사용자 액션 추적 (버튼 클릭, 폼 제출 등)
 * @param {string} action - 액션 이름
 * @param {Object} properties - 액션 속성
 */
export const trackUserAction = (action, properties = {}) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.track('user_action', {
      action,
      timestamp: new Date().toISOString(),
      ...properties
    });
  }
};

/**
 * 성능 메트릭 추적
 * @param {string} metricName - 메트릭 이름
 * @param {number} value - 메트릭 값
 * @param {Object} properties - 추가 속성
 */
export const trackPerformance = (metricName, value, properties = {}) => {
  if (process.env.REACT_APP_DEPLOY_ENV !== 'local') {
    H.track('performance_metric', {
      metric: metricName,
      value,
      ...properties
    });
  }
};

/**
 * 현재 환경 정보 반환
 * @returns {string} 현재 환경 (local, development, production)
 */
export const getCurrentEnvironment = () => {
  return process.env.REACT_APP_DEPLOY_ENV || 'local';
};

/**
 * Highlight.io가 활성화되어 있는지 확인
 * @returns {boolean} 활성화 여부
 */
export const isHighlightEnabled = () => {
  return process.env.REACT_APP_DEPLOY_ENV !== 'local';
};
